"use client";

import { useEffect, useState } from "react";
import type { JobCard } from "@/lib/types";
import EntryForm from "@/components/site/EntryForm";

type Props = {
  jobs: JobCard[];
};

/**
 * 募集要項ページのエントリー枠。
 *
 * `?job=<slug>`（求人詳細の「この求人に応募」から付く）の読み取りを
 * クライアント側に閉じている。ページ側で searchParams を await すると
 * /recruit が動的レンダリングになり、Next 15 は <title> や
 * <meta name="description"> を head ではなくストリームの後ろへ回す。
 * 募集要項は検索から直接入ってくるページなので、静的のまま保ちたい。
 *
 * 読み取りに useSearchParams ではなく useEffect を使っているのは、
 * 静的ページで useSearchParams を呼ぶと Suspense 境界の中身が初期HTMLから
 * 丸ごと消え、応募フォームがJS実行までDOMに現れなくなるため。
 * ここではマウント後に読むだけなので、フォーム自体はサーバー側で描画される。
 */
export default function EntrySection({ jobs }: Props) {
  const [selectedJob, setSelectedJob] = useState<JobCard | null>(null);

  useEffect(() => {
    const slug = new URLSearchParams(window.location.search).get("job");
    setSelectedJob(slug ? jobs.find((j) => j.slug === slug) ?? null : null);
  }, [jobs]);

  return (
    <>
      {selectedJob && (
        <p className="mb-8 text-center text-sm leading-relaxed text-navy-700">
          <span className="font-en text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-text">
            Applying for
          </span>
          <br />
          <span className="font-bold text-navy-900">「{selectedJob.title}」</span>
          に応募します。
        </p>
      )}
      <div className="rounded-xl2 border border-line bg-white p-7 shadow-card md:p-10">
        <EntryForm jobs={jobs} defaultJobSlug={selectedJob?.slug} />
      </div>
    </>
  );
}
